import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useApi, type UserResponse } from '@/shared/api';
import { userKeys } from './queries';
import {
  isUsersFiltered,
  usersFiltersFromSearch,
  usersPageFromSearch,
  usersSearchFromState,
  type UsersFilterState,
  type UsersSearch,
} from './usersListSearch';

export const USERS_PAGE_SIZE = 25;

export type UsersEmptyKind = 'error' | 'filtered' | 'empty' | null;

/**
 * Listenzustand der Benutzerverwaltung: URL-State, Serverabfrage und Mehrfachauswahl.
 *
 * <p>Steht getrennt von der Seite, weil hier die zwei Verträge liegen, die nichts mit Layout zu
 * tun haben: „ein geteilter Link stellt denselben Arbeitskontext her" (§5) und „die Auswahl gilt
 * für das, was auf dem Schirm steht".</p>
 */
export function useUsersList() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as UsersSearch;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filters = usersFiltersFromSearch(search);
  const page = usersPageFromSearch(search);
  const filtered = isUsersFiltered(filters);

  const apply = (next: { filters?: UsersFilterState; page?: number }) =>
    void navigate({
      to: '/users',
      search: usersSearchFromState({
        filters: next.filters ?? filters,
        page: next.page ?? page,
      }) as never,
    });

  const queryArgs = {
    page,
    pageSize: USERS_PAGE_SIZE,
    search: filters.query.trim() || undefined,
    role: filters.role || undefined,
    status: filters.status || undefined,
  };
  const scopeKey = JSON.stringify(queryArgs);

  const { data, isLoading, error } = useQuery({
    queryKey: userKeys.list(queryArgs),
    queryFn: () => api.users.list(queryArgs),
    placeholderData: (prev) => prev,
  });

  // Wechselt Seite oder Filter, wäre eine fortbestehende Auswahl eine Behauptung über Zeilen,
  // die niemand mehr sieht.
  useEffect(() => setSelectedIds(new Set()), [scopeKey]);

  const items: UserResponse[] = data?.items ?? [];
  const listIsEmpty = !isLoading && data !== undefined && items.length === 0;
  const emptyKind: UsersEmptyKind = error
    ? 'error'
    : listIsEmpty && filtered
      ? 'filtered'
      : listIsEmpty && page === 1
        ? 'empty'
        : null;

  return {
    items,
    total: data?.total ?? 0,
    hasData: data !== undefined,
    emptyKind,
    filters,
    filtered,
    page,
    // Ein Filterwechsel setzt auf Seite 1 zurück — sonst landet man auf einer Seite, die es im
    // neuen Ergebnis nicht mehr gibt.
    setFilters: (next: UsersFilterState) => apply({ filters: next, page: 1 }),
    setPage: (next: number) => apply({ page: next }),
    selectedIds,
    selected: items.filter((u) => selectedIds.has(u.id)),
    clearSelection: () => setSelectedIds(new Set()),
    toggle: (id: string) =>
      setSelectedIds((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    toggleAll: () =>
      setSelectedIds((current) =>
        items.every((u) => current.has(u.id)) ? new Set() : new Set(items.map((u) => u.id)),
      ),
  };
}
